import { getStoreState, useAppStore, type AppState } from '../../store';
import { callAction } from '../../app/actions';
import { markNodePosition } from '../../app/mapWrites';
import { prefersReducedMotion } from '../../app/motion';
import { buildGraph } from '../../lib/graph';
import { buildHiddenSets, canDocBtn, isPermHidden } from '../../lib/permissions';
import { isHidden as isHiddenPure } from '../../lib/visibility';
import { canonicalLayout, displayLayout, type LayoutPosition } from '../../lib/layout/layout';
import { resolveCollisions } from '../../lib/layout/collisions';
import { docExtraHeight } from '../../lib/layout/docHeight';
import { LAYOUT } from '../../lib/layout/constants';
import {
  boundsInside,
  cameraDuration,
  cameraFor,
  clampZoom,
  FIT_MAX_ZOOM,
  frameAt,
  MIN_ZOOM,
  type Camera,
  type CameraOpOptions,
} from '../../lib/camera';
import type { LayoutIntent, NodeId } from '../../lib/types';
import { mapContextOf } from './mapContext';
import { posY } from './posY';

/**
 * The map's imperative surface: everything that is asked of the map from OUTSIDE a React
 * render.
 *
 * Three kinds of caller reach it, and they are why this is a module rather than a hook:
 *
 *  - **The vanilla script.** `render()`, `autoLayout()`, `resolveCollisions()`, `fit()` and
 *    `centerOnNodes()` are called from about fifty places in `infostation.html` — the tag
 *    tree, the search, the chatbot, the AI-doc console, the undo stack. Those call sites are
 *    not being rewritten; the FUNCTIONS behind the names are. Each vanilla name is now one
 *    line delegating here, so the seam is a handful of stubs rather than fifty edits.
 *  - **Pointer handlers.** A `pointermove` is not a component. It reads the camera and the
 *    dragged subtree and commits on `pointerup`, and routing that through React state per
 *    frame would reconcile 147 nodes for every mouse movement.
 *  - **`saveViewState()` / `restoreViewState()`.** The camera moved into the store with the
 *    map, and boot is still the vanilla script's, so it needs an explicit way to read and
 *    write it. `getCamera`/`setCamera` are that way, and they are named in both files.
 *
 * WHY LAYOUT IS QUEUED RATHER THAN RUN. `autoLayout()` cannot compute anything at the moment
 * it is called. Expanding a branch makes nodes visible that React has not rendered yet, so
 * their heights are unknown and the pass would place every one of them at the 46px fallback —
 * which is exactly why the vanilla version starts with its own `render()`. Here the request
 * is recorded and `MapStage` drains it in a layout effect: after the render that made the
 * nodes exist, after they were measured, and still before paint. Same ordering, one paint
 * instead of two renders and three measurement sweeps.
 *
 * WHO WRITES POSITIONS. On the React side: `commitPositions()` below, and nothing else. It
 * writes the store's `positions` slice AND `nodes[id].x/y/side/depth`, because the node objects
 * are shared live with the vanilla script and `pushStructuralSync()` still serialises the map
 * from them. One writer, two destinations, in one function.
 *
 * AND SINCE V0.34 IT ALSO DECIDES WHETHER ANYBODY ELSE HEARS ABOUT IT. Every queued op
 * carries a `LayoutIntent`, and `commitPositions` marks a node position-dirty only when the
 * intent is `'structural'` AND the coordinates actually moved. A collapse re-lays the tree
 * exactly as a drag does, and the difference between them is not visible in the numbers —
 * only in why the pass was asked for. See `lib/types.ts` on `LayoutIntent`.
 *
 * THE VANILLA SIDE STILL WRITES POSITIONS TOO, and pretending otherwise would be the dishonest
 * half of that sentence. `reparent()` translates a moved subtree, `addChild()` places a new
 * node, `delNode()`'s undo restores one, and `undoDrag()` puts a whole dragged subtree back —
 * all by assigning to `nodes[id].x/y` directly. That is not a second source of truth that can
 * drift: those four are the ONLY ones, each ends in `render()`, and `store/fromApp.ts`
 * re-derives `positions` from the node objects
 * at exactly that moment. The node objects are the shared representation; the store's slice is
 * the render-time index over it, and the index is rebuilt whenever the representation moves.
 * Both halves go when the vanilla script does.
 */

/** A queued layout operation. See the note above on why these are not run immediately. */
type LayoutOp =
  | { kind: 'layout'; intent: LayoutIntent }
  /** `resolveCollisions(anchorId)` — separate overlapping nodes, holding one subtree fixed. */
  | { kind: 'settle'; anchorId: NodeId | null; intent: LayoutIntent }
  /** Show the expanded rows, or hide them. The flag; the pass follows the drain. */
  | { kind: 'docsExpand'; on: boolean }
  /** Re-derive that flag from the zoom band and the pin — for when the rows' CONTENT
   *  changed under a flag that did not move (V0.42.3). */
  | { kind: 'docsSync' }
  | { kind: 'fit'; opts?: CameraOpOptions }
  | { kind: 'center'; ids: NodeId[]; opts?: CameraOpOptions };

let queue: LayoutOp[] = [];

/**
 * How big the stage is — what `fit()` and `centerOnNodes()` frame the map into.
 *
 * `MapStage` supplies it, but the fallback is not decoration: the ops are drained from a
 * LAYOUT effect and the metrics are registered from a passive one, so on the very first commit
 * the registration has not happened yet. Reading `#stage` directly means a `fit()` queued that
 * early frames the map instead of silently doing nothing.
 */
let stageRect: (() => { width: number; height: number }) | null = null;

export function setStageMetrics(fn: () => { width: number; height: number }): void {
  stageRect = fn;
}

/** Exported (V0.53.4) for the minimap, whose frame is this size divided by the zoom. */
export function stageSize(): { width: number; height: number } | null {
  if (stageRect) return stageRect();
  const el = typeof document === 'undefined' ? null : document.getElementById('stage');
  return el ? { width: el.clientWidth, height: el.clientHeight } : null;
}

function enqueue(op: LayoutOp): void {
  queue.push(op);
  // The counter is what makes React render; the queue is what remembers the work.
  getStoreState().bumpLayoutTick();
}

/** Is there work waiting? `MapStage` asks before deciding whether it needs to measure. */
export const hasPendingLayout = (): boolean => queue.length > 0;

export const requestLayout = (intent: LayoutIntent): void => enqueue({ kind: 'layout', intent });
export const requestSettle = (anchorId: NodeId | null, intent: LayoutIntent): void =>
  enqueue({ kind: 'settle', anchorId: anchorId ?? null, intent });
export const requestDocsExpand = (on: boolean): void => enqueue({ kind: 'docsExpand', on });
/**
 * Re-derive `docsExpanded`, and lay the rows out again either way (V0.42.3).
 *
 * `evaluateDocsExpand()` early-returns when the flag does not move, which is right for the
 * zoom path — but the rows' CONTENT has inputs of its own. Entering edit mode adds the dotted
 * planned pills to every node the viewer may create on, and the heatmap does the same; neither
 * changes WHETHER the rows are showing, so neither would reach the layout through the flag.
 * Queuing this op does, because every drain ends in the display pass.
 */
export const requestDocsSync = (): void => enqueue({ kind: 'docsSync' });
export const requestFit = (opts?: CameraOpOptions): void => enqueue({ kind: 'fit', opts });
export const requestCenter = (ids: NodeId[], opts?: CameraOpOptions): void =>
  enqueue({ kind: 'center', ids, opts });

/* ------------------------------------------------------------------ *
 * Reading the map                                                      *
 * ------------------------------------------------------------------ */

export const rootIdOf = (s: Pick<AppState, 'nodes'>): NodeId | undefined => {
  for (const id in s.nodes) if (s.nodes[id].root) return id;
  return undefined;
};

/** `isHidden()` — permission-hidden, or under a collapsed ancestor. */
export function hiddenPredicate(s: AppState): (id: NodeId) => boolean {
  const hidden = buildHiddenSets(s.perm, s.tagEdges);
  return (id) => isHiddenPure(id, { nodes: s.nodes, edges: s.edges, hidden, forceOpen: s.forceOpen });
}

export function permHiddenPredicate(s: AppState): (id: NodeId) => boolean {
  const hidden = buildHiddenSets(s.perm, s.tagEdges);
  return (id) => isPermHidden(id, s.edges, hidden);
}

/**
 * Measured height, or the same fallback the vanilla `hOf()` used.
 *
 * `ROOTH` for anything not in the DOM is not a guess to improve on: it is what the original
 * places an unmeasured node at, and the parity fixtures are recorded against it.
 */
const heightOf = (sizes: AppState['sizes']) => (id: NodeId) => sizes[id]?.h ?? LAYOUT.ROOTH;

/* ------------------------------------------------------------------ *
 * Writing positions — the one writer                                   *
 * ------------------------------------------------------------------ */

/**
 * Write a layout's positions, and — when the intent says so — record which of them are owed
 * to the server.
 *
 * A node is marked only if its coordinates ACTUALLY changed. A structural pass re-commits
 * every node it placed, most of them at the numbers they already had; marking all of them
 * would put 150 unchanged rows into a delta whose whole purpose is to carry what moved.
 */
export function commitPositions(next: Record<NodeId, LayoutPosition>, intent: LayoutIntent): void {
  const s = getStoreState();
  /**
   * A STRUCTURAL pass publishes; a partial tree may not.
   *
   * `intent === 'structural'` means "these positions are meant for everyone" — a realign, a
   * create, a reparent — and `markNodePosition` puts them in the next delta. The added clause
   * is the V0.43 one: when the server withheld branches from this client, the canonical pass
   * lays the visible siblings out across the gap the hidden ones leave, and publishing that
   * would move nodes under every other user. Drag and create still publish, because the user
   * chose those positions for a subtree they can actually see; it is only the derived,
   * whole-tree tidy that has to stay local.
   *
   * The positions are still WRITTEN below either way — the map this user is looking at should
   * be tidy. They simply stop being announced.
   */
  const publish = intent === 'structural' && !s.mapFiltered;
  for (const id in next) {
    const n = s.nodes[id];
    if (!n) continue;
    const p = next[id];
    if (publish && (n.x !== p.x || n.y !== p.y || n.side !== p.side || n.depth !== p.depth)) {
      markNodePosition(id);
    }
    // The node objects are the app's own, shared live with `app/state.ts`.
    // `pushStructuralSync()` reads x/y/side/depth from them, so they are the durable half of
    // this write; the store's slice is the render-time half. See the header.
    n.x = p.x;
    n.y = p.y;
    n.side = p.side;
    n.depth = p.depth;
  }
  s.setPositions(next);
}

/* ------------------------------------------------------------------ *
 * The passes                                                           *
 * ------------------------------------------------------------------ */

/** `autoLayout()` — the canonical tidy-tree pass, anchored so the root does not move. */
function runCanonical(s: AppState, sizes: AppState['sizes'], intent: LayoutIntent): void {
  const rootId = rootIdOf(s);
  if (!rootId) return;
  const root = s.nodes[rootId];
  const next = canonicalLayout({
    nodes: s.nodes,
    graph: buildGraph(s.edges),
    rootId,
    heightOf: heightOf(sizes),
    isPermHidden: permHiddenPredicate(s),
    forceOpen: s.forceOpen,
    // Where the root already is. This is what keeps the camera still across a realign.
    anchor: { x: root.x, y: root.y },
  });
  commitPositions(next, intent);
}

/** `computeDisplayLayout()` — the ephemeral expanded-rows pass. Writes no canonical y. */
function runDisplay(s: AppState, sizes: AppState['sizes']): Record<NodeId, number> {
  const rootId = rootIdOf(s);
  if (!rootId) return {};
  const docBtn = (perm: Parameters<typeof canDocBtn>[1]) => canDocBtn(s.perm, perm);
  const ctx = mapContextOf(s);
  return displayLayout({
    nodes: s.nodes,
    graph: buildGraph(s.edges),
    rootId,
    heightOf: heightOf(sizes),
    // `plannedVisible` per node (V0.42): the dotted pills reserve height only where they show,
    // so the layout and `beanRowsOf` agree exactly on which nodes gained rows.
    extraHeightOf: (id) =>
      s.nodes[id]
        ? docExtraHeight(s.nodes[id], { expanded: true, canDocBtn: docBtn, plannedVisible: ctx.showPlanned(id) })
        : 0,
    isPermHidden: permHiddenPredicate(s),
    forceOpen: s.forceOpen,
  });
}

/** `resolveCollisions(anchorId)` — the 60-pass separation, with zero DOM reads. */
function runSettle(
  s: AppState,
  sizes: AppState['sizes'],
  anchorId: NodeId | null,
  intent: LayoutIntent,
): void {
  const isHidden = hiddenPredicate(s);
  const ids: NodeId[] = [];
  for (const id in s.nodes) if (!isHidden(id) && !s.nodes[id].root) ids.push(id);
  if (!ids.length) return;

  const y = resolveCollisions({
    nodes: s.nodes,
    graph: buildGraph(s.edges),
    positions: s.positions,
    sizeOf: (id) => sizes[id],
    ids,
    anchorId,
  });

  const next: Record<NodeId, LayoutPosition> = {};
  for (const id in s.positions) {
    const p = s.positions[id];
    next[id] = y[id] !== undefined && y[id] !== p.y ? { ...p, y: y[id] } : p;
  }
  commitPositions(next, intent);
}

/**
 * The bounding box of a set of nodes in world space, exactly as `fit()` computes it.
 *
 * The 150x42 fallback is `fit()`'s own, and is a third distinct pair from the collision
 * resolver's 160x44 and the layout's 46 — preserved rather than unified for the same reason
 * as everywhere else: they place unmeasured nodes differently, and unifying moves them.
 */
export function boundsOf(s: AppState, ids: NodeId[]): { a: number; b: number; c: number; d: number } {
  let a = 1e9;
  let b = 1e9;
  let c = -1e9;
  let d = -1e9;
  for (const id of ids) {
    const box = nodeBoxOf(s, id);
    if (!box) continue;
    a = Math.min(a, box.x);
    c = Math.max(c, box.x + box.w);
    b = Math.min(b, box.y);
    d = Math.max(d, box.y + box.h);
  }
  return { a, b, c, d };
}

/**
 * One node's box in world space, as `fit()` sees it — the root centred on its x, a left-side
 * node ending at its x, and the 150x42 fallback for anything unmeasured. Exported (V0.53.4) so
 * the minimap draws exactly the boxes the camera frames; a second copy of the side rule in
 * the minimap is how its marks would drift a node's width off the map's.
 */
export function nodeBoxOf(
  s: AppState,
  id: NodeId,
): { x: number; y: number; w: number; h: number } | undefined {
  const n = s.nodes[id];
  if (!n) return undefined;
  const size = s.sizes[id];
  const w = size ? size.w : 150;
  const h = size ? size.h : 42;
  const y = posY(s, id);
  const x = s.positions[id]?.x ?? 0;
  const x0 = n.root ? x - w / 2 : (n.side as number) < 0 ? x - w : x;
  return { x: x0, y, w, h };
}

function runFit(s: AppState, opts?: CameraOpOptions): void {
  const isHidden = hiddenPredicate(s);
  const ids: NodeId[] = [];
  for (const id in s.nodes) if (!isHidden(id)) ids.push(id);
  const size = stageSize();
  if (!ids.length || !size) {
    opts?.onEnd?.(false);
    return;
  }
  const target = cameraFor(boundsOf(s, ids), size, { pad: 60, minK: MIN_ZOOM, maxK: FIT_MAX_ZOOM });
  moveCamera(target, {
    animate: opts?.animate,
    onEnd: (completed) => {
      // `fit()` has always ended in a save, and it is the boot-time one: the FIRST write of the
      // durable prefs record in a fresh browser is this call, forty milliseconds after sign-in.
      // It cannot be moved to the stub, because the pass is queued and would run after.
      // LOCAL ONLY since V0.34: framing the map is a camera move and nothing else, and this call
      // is what used to issue a full structural PUT ~840 ms after every fresh sign-in.
      // (`centerOnNodes` never saved, and still does not — see below.)
      //
      // AN ANIMATED FIT SAVES THE VIEW RECORD ONLY (V0.46). The camera lives in the view
      // record regardless: durable prefs have no camera field at all.
      //
      // NEITHER BRANCH BADGES ANY MORE (V0.49.2). V0.46 silenced the animated one because
      // Expand all had already badged the same gesture; the rule is now the general one —
      // framing the map is navigation, and the badge is for things somebody changed. The
      // unanimated branch keeps `saveLocal` rather than `saveViewState` because it is also
      // the BOOT-TIME first write of the durable prefs record in a fresh browser.
      if (!opts?.animate) callAction('saveLocal', { quiet: true });
      else if (completed) callAction('saveViewState');
      opts?.onEnd?.(completed);
    },
  });
}

/** `centerOnNodes()` — the chatbot's "show me the nodes I cited". Wider pad, higher floor. */
function runCenter(s: AppState, wanted: NodeId[], opts?: CameraOpOptions): void {
  const isHidden = hiddenPredicate(s);
  const vis = wanted.filter((id) => s.nodes[id] && !isHidden(id));
  const size = stageSize();
  if (!vis.length || !size) {
    opts?.onEnd?.(false);
    return;
  }
  const b = boundsOf(s, vis);
  // "Only move if you have to" (V0.46), which is what keeps expanding a branch whose children
  // were never off-screen from jolting the map for no reason.
  if (opts?.onlyIfOutside && boundsInside(b, size, s.view)) {
    opts?.onEnd?.(false);
    return;
  }
  const target = cameraFor(b, size, {
    pad: 120,
    minK: 0.35,
    maxK: FIT_MAX_ZOOM,
    zoomIn: opts?.zoomIn,
    currentK: s.view.k,
  });
  moveCamera(target, { animate: opts?.animate, onEnd: opts?.onEnd });
}

/**
 * Drain the queue. Called by `MapStage` from a layout effect, with the sizes it just measured
 * rather than the ones in the store — the store write and this pass land in the same commit,
 * and reading it back would be reading the previous frame's measurements.
 */
export function runPendingLayout(sizes: AppState['sizes']): boolean {
  if (!queue.length) return false;

  // NOTHING IS DRAINED BEFORE THERE IS A MAP, and the queue is LEFT INTACT rather than run
  // against an empty graph. This is not defensive coding; it is the ordering the boot sequence
  // actually has. `enterApp()` calls `autoLayout()` several steps before `connectReactBridge()`
  // installs the snapshot provider, so the request arrives while the store still holds nothing.
  // Draining it there produced a layout of no nodes, emptied the queue, and left the real map —
  // which arrived moments later — sitting at whatever positions the server happened to hold.
  // The map was correct in every functional sense and untidy in every visual one, which is
  // exactly the class of defect `35-map.spec.ts`'s live layout check exists to catch.
  if (!rootIdOf(getStoreState())) return false;

  const ops = queue;
  queue = [];
  /**
   * THE CAMERA GOES LAST, AFTER THE DISPLAY PASS (V0.46).
   *
   * `boundsOf` reads `posY`, and `posY` answers `displayY` whenever the documentation rows are
   * showing — but `displayY` is recomputed at the END of this function, so a camera op run in
   * the loop below frames nodes that appeared this drain at coordinates the display pass has
   * not produced yet, and every y beneath them at last frame's. The map then re-lays out
   * underneath a camera that has already settled.
   *
   * Invisible until now because framing was rare. Expanding a branch makes it the common path.
   */
  const cameraOps: LayoutOp[] = [];
  for (const op of ops) {
    const s = getStoreState();
    switch (op.kind) {
      case 'layout':
        runCanonical(s, sizes, op.intent);
        break;
      case 'settle':
        runSettle(s, sizes, op.anchorId, op.intent);
        break;
      case 'docsSync': {
        // Re-derive from the same two inputs `evaluateDocsExpand()` folds together. Setting
        // the flag is ALL this does; the pass below is what lays the rows out.
        const byZoom = s.docsExpanded ? s.view.k >= DOCS_EXPAND_K_OFF : s.view.k >= DOCS_EXPAND_K_ON;
        s.setDocsExpanded(s.docsPinned || byZoom);
        break;
      }
      case 'docsExpand':
        // `op.on` IS "the rows are showing" — `evaluateDocsExpand()` has already folded the
        // zoom threshold and the pin together, so there is one condition here, not two.
        s.setDocsExpanded(op.on);
        break;
      case 'fit':
      case 'center':
        cameraOps.push(op);
        break;
    }
  }

  /**
   * THE DISPLAY PASS FOLLOWS THE CANONICAL ONE, ALWAYS (V0.42.3).
   *
   * The expanded rows are `position:absolute`, so they add nothing to a node's measured box
   * and the clearance between nodes comes from this pass alone. That makes it a function of
   * everything a layout op can move — the graph, the collapse state, the measured sizes, and
   * which dotted planned pills are drawn — so running it anywhere but here means enumerating
   * those inputs, and missing one is invisible until the rows land on top of a node. Three
   * bugs came out of that enumeration (heatmap-unzoomed, the edit-mode switch, and Expand all
   * with the rows showing); running it once at the end of every drain retires the question.
   *
   * It is not a cost worth avoiding: a drain happens on a user gesture, `displayLayout()` is
   * O(n) with no DOM reads, and `setDisplayY()` compares by value — an unchanged answer keeps
   * the container's identity and re-renders nothing.
   */
  const after = getStoreState();
  after.setDisplayY(after.docsExpanded ? runDisplay(after, sizes) : {});

  for (const op of cameraOps) {
    const s = getStoreState();
    if (op.kind === 'fit') runFit(s, op.opts);
    else if (op.kind === 'center') runCenter(s, op.ids, op.opts);
  }
  return true;
}

/* ------------------------------------------------------------------ *
 * The camera                                                           *
 * ------------------------------------------------------------------ */

/**
 * Documentation-row expansion thresholds, with hysteresis.
 *
 * Two numbers rather than one so the rows do not flicker in and out while somebody rests on
 * the boundary: they appear at 1.5 and only disappear again below 1.3.
 */
export const DOCS_EXPAND_K_ON = 1.5;
export const DOCS_EXPAND_K_OFF = 1.3;

let docsExpandTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Every write of the zoom goes through here, so the expansion check lives in one place.
 *
 * Debounced past a wheel-gesture burst, and — carried over verbatim — it NEVER persists. A
 * zoom is view-only; making it durable would mean a `PUT /api/map` per wheel notch.
 */
export function setZoomK(k: number): void {
  getStoreState().setZoomK(k);
  if (docsExpandTimer) clearTimeout(docsExpandTimer);
  docsExpandTimer = setTimeout(evaluateDocsExpand, 150);
}

/**
 * THE ONE PLACE "are the documentation rows showing" is decided (V0.42.2).
 *
 * Two inputs: the zoom threshold (with its hysteresis band, so resting on the boundary
 * cannot flicker) and the `docsPinned` toggle, which shows them at any zoom. Everything
 * downstream — `posY()`, the rendered rows, the drag's collapse to canonical space — reads
 * the single `docsExpanded` flag this writes. That is deliberate: V0.42 spelled the
 * condition out at four call sites, updated three of them, and the fourth (`posY`) quietly
 * kept returning compact coordinates while the rows rendered over the top.
 */
export function evaluateDocsExpand(): void {
  const s = getStoreState();
  const byZoom = s.docsExpanded ? s.view.k >= DOCS_EXPAND_K_OFF : s.view.k >= DOCS_EXPAND_K_ON;
  const want = s.docsPinned || byZoom;
  if (want === s.docsExpanded) return;
  requestDocsExpand(want);
}

export const getCamera = () => ({ ...getStoreState().view });

export function setCamera(v: Camera, opts?: { animate?: boolean }): void {
  // Through the animator since V0.46, which is also what makes a restore CANCEL an in-flight
  // move rather than race it. Instant unless asked otherwise, as it has always been.
  moveCamera({ x: v.x, y: v.y, k: clampZoom(v.k) }, { animate: opts?.animate });
}

/** Zoom about the stage's centre — the two buttons beside the map. */
export function zoomBy(factor: number): void {
  const size = stageSize();
  if (!size) return;
  const s = getStoreState();
  const { width, height } = size;
  const cx = width / 2;
  const cy = height / 2;
  const wx = (cx - s.view.x) / s.view.k;
  const wy = (cy - s.view.y) / s.view.k;
  const k = clampZoom(s.view.k * factor);
  setZoomK(k);
  getStoreState().panTo(cx - wx * k, cy - wy * k);
}

/**
 * Zoom about a point on the stage — the wheel.
 *
 * `cx`/`cy` are stage-relative, which is what keeps the point under the pointer fixed.
 */
export function zoomAt(cx: number, cy: number, factor: number): void {
  const s = getStoreState();
  const wx = (cx - s.view.x) / s.view.k;
  const wy = (cy - s.view.y) / s.view.k;
  const k = clampZoom(s.view.k * factor);
  setZoomK(k);
  getStoreState().panTo(cx - wx * k, cy - wy * k);
}

/** Subscribe to the camera from outside React — how the transform is applied without a render. */
export const subscribeCamera = (fn: (view: AppState['view']) => void): (() => void) =>
  useAppStore.subscribe((s) => s.view, fn);

/* ------------------------------------------------------------------ *
 * Moving the camera deliberately (V0.46)                               *
 * ------------------------------------------------------------------ */

/**
 * ONE ANIMATOR, and everything that moves the camera goes through it.
 *
 * Not per call site, and not a CSS transition. Three properties decide the shape:
 *
 * 1. **It drives the store, one write per frame.** `setView` rather than `setZoomK` + `panTo`,
 *    which would be two commits per frame and would re-arm the documentation-row debounce
 *    thirty-six times over a single move. Nothing subscribes to `view` through a hook, so a
 *    frame costs exactly what a `pointermove` pan costs today: two direct DOM writes and no
 *    React render at all. A CSS transition would have been cheaper still and was never an
 *    option — `#world`'s inline transform string is the only camera the regression suite can
 *    read, and a transition leaves it holding the destination from the first millisecond.
 *
 * 2. **IT CANCELS ITSELF BY WATCHING, NOT BY BEING TOLD.** The alternative was a
 *    `cancelCameraMove()` at the top of every gesture handler — the pan, the drag, the wheel,
 *    the zoom buttons, `setCamera`, the instant `fit()` — six hand-placed calls that have to
 *    be six forever, where the seventh caller silently gets a camera that fights it. Instead
 *    the move subscribes to the camera and stops the moment a write arrives that it did not
 *    make. `writing` is the whole mechanism: zustand notifies synchronously inside `set()`, so
 *    a notification outside that window is, by construction, somebody else.
 *
 * 3. **`prefers-reduced-motion` ARRIVES, it does not hurry.** Skipping to the target is the
 *    honest reading of the setting; a shortened animation is still an animation.
 */
interface CameraMove {
  from: Camera;
  to: Camera;
  start: number;
  /** This move's own length — `cameraDuration(from, to)`, not a constant. */
  ms: number;
  frame: number;
  /** A hidden tab suspends rAF; this lands the move regardless. See `flight.ts` for the twin. */
  deadline: ReturnType<typeof setTimeout>;
  stop: () => void;
  onEnd?: (completed: boolean) => void;
}

let move: CameraMove | null = null;
/** True only across this module's own `setView`. See note 2 above. */
let writing = false;

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

function writeView(v: Camera): void {
  writing = true;
  try {
    getStoreState().setView(v);
  } finally {
    writing = false;
  }
}

function finish(completed: boolean): void {
  const m = move;
  if (!m) return;
  move = null;
  if (m.frame) cancelAnimationFrame(m.frame);
  clearTimeout(m.deadline);
  m.stop();
  // Arm the documentation-row evaluation exactly as a wheel notch does, whether the move
  // landed or was cut short: the zoom has changed either way, and the store write inside this
  // no-ops because the value is already there. Done AFTER the subscription is dropped, so it
  // cannot read as somebody else moving the camera.
  setZoomK(getStoreState().view.k);
  m.onEnd?.(completed);
}

/** Stop an in-flight move where it stands. Its `onEnd` is called with `false`. */
export function cancelCameraMove(): void {
  finish(false);
}

/** Is the camera mid-move? */
export const cameraMoving = (): boolean => move !== null;

/**
 * Put the camera at `to` — instantly, or over `CAMERA_MS` when asked.
 *
 * `onEnd` is called exactly once on every path, including the instant one and the
 * already-there one, so a caller may sequence on it without a timeout of its own.
 */
export function moveCamera(to: Camera, opts: { animate?: boolean; onEnd?: (c: boolean) => void } = {}): void {
  cancelCameraMove();
  const from = { ...getStoreState().view };
  // How long THIS trip takes: the full 600 ms for a full trip, less for a short hop, nothing
  // for a move too small to see — which is what stops a click on a node already in the
  // middle waiting out an animation of nothing before its popup may appear.
  const ms = cameraDuration(from, to);
  const instant =
    !opts.animate || ms === 0 || prefersReducedMotion() || typeof requestAnimationFrame === 'undefined';

  if (instant) {
    writeView(to);
    setZoomK(getStoreState().view.k);
    opts.onEnd?.(true);
    return;
  }

  const m: CameraMove = {
    from,
    to: { ...to },
    start: now(),
    ms,
    frame: 0,
    // Lands the move if the frames never come — a hidden tab suspends rAF entirely, and every
    // caller that sequences on `onEnd` (the popup, the page) would otherwise wait forever.
    deadline: setTimeout(() => {
      if (move !== m) return;
      writeView(m.to);
      finish(true);
    }, ms + 200),
    stop: () => {},
    onEnd: opts.onEnd,
  };
  move = m;
  m.stop = subscribeCamera(() => {
    if (!writing) finish(false);
  });

  const step = (): void => {
    if (move !== m) return;
    const { view, done } = frameAt(m.from, m.to, now() - m.start, m.ms);
    writeView(view);
    if (move !== m) return; // a subscriber cancelled us from inside that write
    if (done) finish(true);
    else m.frame = requestAnimationFrame(step);
  };
  m.frame = requestAnimationFrame(step);
}
